import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { Slot } from "@radix-ui/react-slot";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-neutral-950 dark:focus-visible:ring-neutral-300 inline-flex items-center justify-center h-11 rounded-default text-nowrap defaultTransition",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-11 px-4 py-2",
        icon: "h-10 w-10",
        lg: "h-11 rounded-md px-8",
        sm: "h-9 rounded-md px-3",
      },
      variant: {
        default:
          "bg-neutral-900 text-neutral-50 hover:bg-neutral-900/90 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50/90",
        destructive:
          "bg-red-500 text-neutral-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-neutral-50 dark:hover:bg-red-900/90",
        ghost:
          "hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
        link: "text-neutral-900 underline-offset-4 hover:underline dark:text-neutral-50",
        outline:
          "border border-neutral-200 bg-background/primary hover:bg-neutral-100 hover:text-neutral-900 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
        primary: `
        drop-shadow-card
        hover:drop-shadow-tooltip
        active:drop-shadow-none
        disabled:drop-shadow-none
  
        bg-button/primary/background/default 
        text-button/primary/label/default 
        hover:bg-button/primary/background/hover 
        active:bg-button/primary/background/pressed 
        disabled:bg-background/brand/opacity 
        disabled:text-text/disabled 
      `,
        secondary: `
        shadow-card
        hover:shadow-tooltip
        active:shadow-none
        disabled:shadow-none

        bg-button/secondary/background/default 
        text-button/secondary/label/default 
        border border-button/secondary/border/default 
        hover:bg-button/secondary/background/hover 
        hover:border-button/secondary/border/hover 
        active:bg-button/secondary/background/pressed 
        active:border-button/secondary/border/pressed 
        disabled:text-text/disabled 
        disabled:border-background/brand/opacity
      `,
        tertiary: `
          text-button/tertiary/foreground/default 
          hover:text-button/tertiary/foreground/hover 
          active:text-button/tertiary/foreground/pressed 
          disabled:text-text/disabled 
        `,
        activePagination: `
          body-base-medium
          bg-background/primary
          text-text/primary
          border-border/primary
          border-1
          hover:bg-background/component
          h-8 sm:h-10
          w-8 sm:w-10
          `,
        unActivePagination: `
          body-base-medium
          text-text/disabled
          hover:bg-background/component
          h-8 sm:h-10
          w-8 sm:w-10
          `,
      },
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, variant, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cnTwMerge(buttonVariants({ className, size, variant }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
